import { useCallback, useEffect, useRef } from "react";
import { useGummiwormContext } from "@/context/GummiwormContext";

/**
 * Returns a function that produces the next nonce for L2 transactions.
 *
 * Tracks a local offset that increments on each call, so rapid successive
 * actions don't collide. The offset resets when the account's on-chain
 * nonce catches up (i.e. SpacetimeDB reflects the new nonce).
 */
export function useNextNonce(): () => number | null {
  const { accounts, myAccountId } = useGummiwormContext();
  const myAccount = myAccountId != null
    ? accounts.find((a) => a.accountId === myAccountId)
    : null;

  const dbNonce = myAccount ? Number(myAccount.nonce) : 0;
  const localNonceRef = useRef(dbNonce);

  // When the DB nonce catches up or surpasses our local nonce, reset
  useEffect(() => {
    if (dbNonce >= localNonceRef.current) {
      localNonceRef.current = dbNonce;
    }
  }, [dbNonce]);

  return useCallback(() => {
    if (!myAccount) return null;
    localNonceRef.current += 1;
    return localNonceRef.current;
  }, [myAccount]);
}
